import {fetchAndActivate, getValue} from "firebase/remote-config";
// @ts-ignore -- "FirebaseUtils" is resolved by the web bundler, not by tsc
import {remoteConfig} from "FirebaseUtils";

export interface SubscriptionTiersConfig {
  [tier: string]: {name?: string};
}

class Configuration {
  subscriptionTiersConfig: SubscriptionTiersConfig;

  constructor(remoteConfig: any) {
    this.subscriptionTiersConfig = this._parseSubscriptionTiers(remoteConfig);
  }

  _parseSubscriptionTiers(remoteConfig: any): SubscriptionTiersConfig {
    const defaultConfig = {
      premium: {name: "Pro"},
      ultra: {name: "Ultra"}
    };

    try {
      // Try correct spelling first
      let value = getValue(remoteConfig, "subscription_tiers");
      let stringValue = value.asString();
      let source = value.getSource();

      // If empty or default, try the typo version (matching iOS)
      if (!stringValue || stringValue === "" || source === "default") {
        value = getValue(remoteConfig, "subcription_tiers");
        stringValue = value.asString();
        source = value.getSource();
      }

      if (stringValue && stringValue !== "" && source !== "default") {
        const parsed = JSON.parse(stringValue);
        return parsed;
      }
    } catch (error) {
      console.error("Error parsing subscription tiers:", error);
    }

    return defaultConfig;
  }
}

class RemoteConfigurationManager {
  configuration: {subscriptionTiersConfig: SubscriptionTiersConfig};
  isInitialized: boolean;
  initPromise: Promise<void> | null;

  constructor() {
    // Configure Remote Config settings
    remoteConfig.settings.minimumFetchIntervalMillis = process.env.NODE_ENV === "development" ? 0 : 3600000;

    // Initialize with default configuration immediately
    this.configuration = this._createDefaultConfiguration();
    this.isInitialized = false;
    this.initPromise = null;
  }

  _createDefaultConfiguration(): {subscriptionTiersConfig: SubscriptionTiersConfig} {
    // Create a minimal config object with defaults
    return {
      subscriptionTiersConfig: {
        premium: {name: "Pro"},
        ultra: {name: "Ultra"}
      }
    };
  }

  configure(): Promise<void> {
    if (this.initPromise) {
      return this.initPromise;
    }

    if (this.isInitialized) {
      return Promise.resolve();
    }

    this.initPromise = this._reloadConfiguration();
    return this.initPromise;
  }

  async _reloadConfiguration(): Promise<void> {
    try {
      await fetchAndActivate(remoteConfig);
      this.configuration = new Configuration(remoteConfig);
      this.isInitialized = true;
    } catch (error) {
      this.configuration = new Configuration(remoteConfig);
      this.isInitialized = true;
    } finally {
      this.initPromise = null;
    }
  }

  reloadConfiguration(): Promise<void> {
    this.isInitialized = false;
    this.initPromise = null;
    return this.configure();
  }

  getSubscriptionTierName(tier: string): string {
    const tierConfig = this.configuration.subscriptionTiersConfig[tier];
    return tierConfig?.name || tier;
  }
}

const remoteConfigurationManager = new RemoteConfigurationManager();
export default remoteConfigurationManager;
